import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, ShieldStar, Heart, ChatCircle, Crown, SealCheck, Trash, Prohibit, CurrencyInr } from "@phosphor-icons/react";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-brand-line p-6">
      <Icon size={22} weight="duotone" className="text-brand-terracotta" />
      <div className="mt-4 font-serif text-4xl text-brand-ink tracking-tighter num-lining">{value ?? 0}</div>
      <div className="mt-1 label-eyebrow text-brand-muted">{label}</div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [q, setQ] = useState("");

  const loadStats = async () => {
    const { data } = await api.get("/admin/stats");
    setStats(data);
  };
  const loadUsers = async (search = "") => {
    const { data } = await api.get("/admin/users", { params: search ? { q: search } : {} });
    setUsers(data.users || []);
  };
  const loadPayments = async () => {
    const { data } = await api.get("/admin/payments");
    setPayments(data.payments || []);
  };

  useEffect(() => { loadStats(); loadUsers(); loadPayments(); }, []);

  const verify = async (id) => {
    await api.post(`/admin/users/${id}/verify`);
    toast.success("Verified");
    loadUsers(q);
  };
  const setStatus = async (id, status) => {
    await api.post(`/admin/users/${id}/status`, null, { params: { status } });
    toast.success(`Status → ${status}`);
    loadUsers(q);
  };
  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/admin/users/${id}`);
    toast.success("User deleted");
    loadUsers(q);
    loadStats();
  };

  const approvePayment = async (id) => {
    await api.post(`/admin/payments/${id}/approve`);
    toast.success("Payment approved and plan activated");
    loadPayments(); loadStats();
  };
  const rejectPayment = async (id) => {
    if (!window.confirm("Reject this payment claim?")) return;
    await api.post(`/admin/payments/${id}/reject`);
    toast.success("Payment rejected");
    loadPayments();
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10" data-testid="admin-page">
      <div className="flex items-center gap-3">
        <ShieldStar size={28} weight="duotone" className="text-brand-terracotta" />
        <div>
          <div className="label-eyebrow text-brand-terracotta">Enterprise Panel</div>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tighter text-brand-ink">Admin</h1>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
        <Stat icon={Users} label="Total members" value={stats.total_users} />
        <Stat icon={SealCheck} label="Verified" value={stats.verified} />
        <Stat icon={Heart} label="Total interests" value={stats.interests_total} />
        <Stat icon={Crown} label="Paid subscribers" value={stats.paid_subscribers} />
        <Stat icon={ChatCircle} label="Messages" value={stats.messages_total} />
        <Stat icon={Users} label="Men" value={stats.male} />
        <Stat icon={Users} label="Women" value={stats.female} />
        <Stat icon={Heart} label="Accepted matches" value={stats.accepted_matches} />
      </div>

      <div className="mt-12 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow text-brand-terracotta">Members</div>
          <h2 className="font-serif text-3xl mt-2 text-brand-ink">Manage members</h2>
        </div>
        <Input
          value={q} onChange={(e) => setQ(e.target.value)} onBlur={() => loadUsers(q)}
          onKeyDown={(e) => { if (e.key === "Enter") loadUsers(q); }}
          placeholder="Search by name or email…"
          data-testid="admin-search"
          className="max-w-sm h-12 rounded-none border-brand-line"
        />
      </div>

      <div className="mt-6 bg-white border border-brand-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-bone">
            <tr className="text-left">
              {["Name", "Email", "Gender", "Plan", "Verified", "Status", "Actions"].map((h) => (
                <th key={h} className="px-4 py-3 label-eyebrow text-brand-muted">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-brand-line" data-testid={`admin-user-${u.id}`}>
                <td className="px-4 py-3 text-brand-ink">{u.name}</td>
                <td className="px-4 py-3 text-brand-muted">{u.email}</td>
                <td className="px-4 py-3 capitalize">{u.gender}</td>
                <td className="px-4 py-3 uppercase text-xs">{u.plan || "free"}</td>
                <td className="px-4 py-3">{u.verified ? <SealCheck size={16} className="text-brand-terracotta" weight="fill" /> : <span className="text-brand-muted">—</span>}</td>
                <td className="px-4 py-3 uppercase text-xs">{u.status || "active"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    {!u.verified && <Button size="sm" onClick={() => verify(u.id)} data-testid={`verify-${u.id}`} className="rounded-full h-8 bg-brand-terracotta text-white hover:bg-brand-terracotta/90">Verify</Button>}
                    {u.status !== "blocked" ? (
                      <Button size="sm" variant="outline" onClick={() => setStatus(u.id, "blocked")} data-testid={`block-${u.id}`} className="rounded-full h-8 border-brand-line">
                        <Prohibit size={14} className="mr-1" /> Block
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setStatus(u.id, "active")} data-testid={`unblock-${u.id}`} className="rounded-full h-8 border-brand-line">
                        Activate
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => del(u.id)} data-testid={`delete-${u.id}`} className="rounded-full h-8 border-brand-line text-brand-terracotta">
                      <Trash size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-16 text-center text-brand-muted" data-testid="admin-users-empty">No members found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Payments */}
      <div className="mt-16">
        <div className="flex items-end gap-3">
          <CurrencyInr size={26} weight="duotone" className="text-brand-terracotta" />
          <div>
            <div className="label-eyebrow text-brand-terracotta">Revenue</div>
            <h2 className="font-serif text-3xl mt-1 text-brand-ink">Payments</h2>
          </div>
        </div>
        <div className="mt-6 bg-white border border-brand-line overflow-x-auto" data-testid="admin-payments-table">
          <table className="w-full text-sm">
            <thead className="bg-brand-bone">
              <tr className="text-left">
                {["Member", "Plan", "Amount", "Method", "Reference / ID", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 label-eyebrow text-brand-muted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-t border-brand-line align-top" data-testid={`admin-payment-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="text-brand-ink">{p.user_name}</div>
                    <div className="text-xs text-brand-muted">{p.user_email}</div>
                  </td>
                  <td className="px-4 py-3 uppercase text-xs">{p.plan}</td>
                  <td className="px-4 py-3">₹{(p.amount || 0) / 100}</td>
                  <td className="px-4 py-3 capitalize">{p.provider}</td>
                  <td className="px-4 py-3 text-xs text-brand-muted break-all">
                    {p.transaction_ref || p.payment_id || p.order_id || "—"}
                    {p.payer_name && <div className="text-brand-ink">Paid by: {p.payer_name}</div>}
                    {p.note && <div className="italic">{p.note}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-[10px] uppercase tracking-widest ${
                      p.status === "captured" || p.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                      p.status === "pending_verification" ? "bg-brand-gold/20 text-brand-gold" :
                      p.status === "rejected" || p.status === "verify_failed" ? "bg-red-100 text-red-700" :
                      "bg-brand-line text-brand-muted"
                    }`}>{p.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    {p.status === "pending_verification" && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => approvePayment(p.id)} data-testid={`approve-${p.id}`} className="rounded-full h-8 bg-brand-terracotta text-white hover:bg-brand-terracotta/90">Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => rejectPayment(p.id)} data-testid={`reject-${p.id}`} className="rounded-full h-8 border-brand-line">Reject</Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {payments.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-16 text-center text-brand-muted" data-testid="admin-payments-empty">No payments yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
